import React, { useState, useEffect } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface WebDaySelectorProps {
  onToggle: (selectedDays: number[]) => void;
  isFirst: boolean;
  initialValue: number[];
}

const WebDaySelector: React.FC<WebDaySelectorProps> = ({
  onToggle,
  isFirst,
  initialValue,
}) => {
  const theme = useTheme();
  const [selectedDays, setSelectedDays] = useState<number[]>(initialValue);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // For first selector, show all days. For additional selectors, only show unselected days
  const availableDays = isFirst ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4, 5, 6];

  useEffect(() => {
    setSelectedDays(initialValue);
  }, [initialValue]);

  const handleDayToggle = (day: number) => {
    let newSelectedDays: number[];

    if (selectedDays.includes(day)) {
      newSelectedDays = selectedDays.filter((d) => d !== day);
    } else {
      newSelectedDays = [...selectedDays, day].sort();
    }

    setSelectedDays(newSelectedDays);
    onToggle(newSelectedDays);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          color: theme.custom.text,
          fontWeight: 500,
        }}
      >
        Set the days you want to be available
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {availableDays.map((day) => {
          const isSelected = selectedDays.includes(day);

          return (
            <Chip
              key={day}
              label={dayNames[day]}
              onClick={() => handleDayToggle(day)}
              variant={isSelected ? 'filled' : 'outlined'}
              sx={{
                backgroundColor: isSelected
                  ? theme.custom.primary
                  : 'transparent',
                color: isSelected ? theme.custom.textCTA : theme.custom.text,
                borderColor: isSelected
                  ? theme.custom.primary
                  : theme.custom.stroke,
                '&:hover': {
                  backgroundColor: isSelected
                    ? theme.custom.primaryDark
                    : theme.custom.fill1,
                },
                minWidth: '48px',
                cursor: 'pointer',
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default WebDaySelector;
